/**
 * Compare: our provision beside the same subject in every other instrument.
 *
 * Three properties carry the whole screen, and each is there because its
 * absence would mislead someone preparing to argue.
 *
 * **One row per comparison unit, not per clause.** Clause 24 runs personal,
 * carer's and compassionate leave across nine units, and against Award 2020
 * those nine hold different answers — some we win, some are level, some the
 * Award never addresses. A single row per clause would have to pick one, and
 * whichever it picked would be a sentence about a dozen differences that could
 * be checked against none of them.
 *
 * **Unchecked says unchecked.** Three tidy columns imply somebody looked. At
 * the time of writing nobody has looked at any of the 360, so a cell that is
 * merely the pipeline's opinion says so where the eye lands, not in a footnote.
 * The citation contract is that a claim rests on a checked comparison; a screen
 * that lets an unchecked one read as settled breaks it more quietly than a
 * wrong verdict would.
 *
 * **Silence is a finding, not an empty cell.** "They have nothing on this" is
 * where claims come from — it is the single most useful thing this corpus
 * knows — so it is stated as a result rather than rendered as a gap that looks
 * like missing data.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FilterBox, useFilter } from '../shell/Filter.js';
import { api, type ComparatorCell, type CompareIndexRow } from '../lib/api.js';
import { EquivalenceBanner } from './EquivalenceBanner.js';

const VERDICT_LABELS: Record<string, string> = {
  primary_better: 'We are ahead',
  comparable: 'Materially the same',
  comparator_better: 'They are ahead',
  no_primary_equivalent: 'We have nothing on this',
  no_comparator_equivalent: 'They have nothing on this',
};

/** Verdict → the design system's meaning colours. */
const VERDICT_COLOUR: Record<string, string> = {
  primary_better: 'var(--color-win)',
  comparable: 'var(--color-muted)',
  comparator_better: 'var(--color-behind)',
  no_primary_equivalent: 'var(--color-defect)',
  no_comparator_equivalent: 'var(--color-silent)',
};

function VerdictChip({ verdict }: { verdict: string | null }) {
  if (!verdict) {
    return (
      <span className="label text-[11px] text-[var(--color-muted)]">Not yet compared</span>
    );
  }
  return (
    <span
      className="label text-[11px] font-medium"
      style={{ color: VERDICT_COLOUR[verdict] ?? 'var(--color-muted)' }}
    >
      {VERDICT_LABELS[verdict] ?? verdict}
    </span>
  );
}

function Cell({ cell }: { cell: ComparatorCell }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 border-l border-[var(--color-rule)] p-3 first:border-l-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="label truncate">{cell.shortName}</span>
        {/* The stamp stays at 11px while the instrument name drops to the
            label's native 9px. The name is orientation; whether anybody has
            checked the row is the finding, and it should out-rank the name of
            the document it is about. */}
        {cell.verified ? (
          <span
            className="label shrink-0 text-[11px] text-[var(--color-win)]"
            title={`Checked by ${cell.verifiedBy}`}
          >
            ✓ checked
          </span>
        ) : (
          /*
           * Said plainly, in the defect colour, on every unchecked cell. This
           * is the most repeated element on the screen and that is the point:
           * 360 of 360 are unchecked, and a screen that whispered it would
           * teach the opposite of what is true.
           */
          <span className="label shrink-0 text-[11px] text-[var(--color-defect)]">unchecked</span>
        )}
      </div>

      <VerdictChip verdict={cell.verdict} />

      {cell.matchedNumber && (
        /*
         * Stated, not linked. This number belongs to a comparator and Read
         * mode holds only the agreement, so the link 404'd where no clause of
         * ours shares the number and — worse — opened a different provision of
         * a different instrument where one does. Verify.tsx fixed exactly this
         * and the fix did not travel here.
         */
        <p className="clause-number text-[12px] leading-snug">
          {cell.matchedNumber}
          {cell.matchedHeading ? ` · ${cell.matchedHeading}` : ''}
        </p>
      )}

      {cell.deltaNote && (
        <p className="text-[12px] leading-snug text-[var(--color-muted)]">{cell.deltaNote}</p>
      )}

      {cell.matchedText && (
        <details className="mt-0.5">
          <summary className="hit label cursor-pointer">Their words</summary>
          {/* Verbatim, in the reading register at the size the mockup sets the
              comparison cells — 13.5px serif. Nothing derived is ever written
              into this, so it is the one thing on the screen a rep can quote,
              and it was set two points smaller than the delta note beside it. */}
          <p className="register-reading mt-1.5 text-[13.5px] leading-relaxed">
            {cell.matchedText}
          </p>
        </details>
      )}
    </div>
  );
}

export function Compare({
  selected,
  onSelect,
  onOpenClause,
}: {
  selected: string | null;
  onSelect: (number: string) => void;
  onOpenClause: (number: string) => void;
}) {
  const index = useQuery({ queryKey: ['compareIndex'], queryFn: api.compareIndex });
  const [open, setOpen] = useState<string | null>(selected);
  const active = open ?? selected;

  const view = useQuery({
    queryKey: ['compare', active],
    queryFn: () => api.compare(active!),
    enabled: Boolean(active),
  });

  const allRows: CompareIndexRow[] = index.data ?? [];
  /*
   * Number and heading only. The verdict counts on each row are numbers, and
   * including them would mean typing "3" survives every row with three
   * adjudications — a filter matching on tallies is a filter that appears to
   * work and does something else.
   */
  const { query, setQuery, filtered: rows, total } = useFilter(allRows, (row) => [
    row.number,
    row.heading,
  ]);

  /*
   * The early return sits below the hooks, not above them. Same reason as the
   * protect board: a hook after a conditional return is called on some renders
   * and not others, and React takes the whole surface down rather than
   * degrading the filter.
   */
  if (index.isLoading)
    return <p className="screen text-[13px] text-[var(--color-muted)]">Loading…</p>;

  return (
    /*
      `.screen` for the 28px inset. The column inside it is 1180px rather than
      `.screen-column`'s 760: this surface's unit is a four-instrument row —
      our words, two Awards and a verdict — and at 760px each of those columns
      is under 180px, which is a column of two-word fragments rather than of
      quotable provisions.
    */
    <div className="screen">
      <div className="mx-auto max-w-[1180px]">
        <EquivalenceBanner />

        {!active && (
          <>
            <h1 className="mb-1 text-[15px] font-medium">Compare</h1>
            <p className="measure-wide mb-5 text-[13px] leading-relaxed text-[var(--color-muted)]">
              Every provision of the agreement against the Award floor, the NSW public Award and
              the support-staff agreement. Where they have nothing, that silence is where a claim
              starts.
            </p>
            {/*
              The rule stays on the <li> rather than moving to `.row`.

              `.row:last-child` drops the final hairline by asking whether the
              row is its parent's last child, and a row that is a button inside
              a list item is always its own parent's only child — so every rule
              in the list would disappear. Putting the padding on the <li>
              instead would take it out of the hit area on a list of 100+ rows.
            */}
            <FilterBox
              label="Filter provisions by clause number or heading"
              value={query}
              onChange={setQuery}
              total={total}
              shown={rows.length}
              noun="provisions"
            />

            <ul className="border-t border-[var(--color-rule)]">
              {rows.map((row) => (
                <li key={row.number} className="border-b border-[var(--color-rule)]">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(row.number);
                      onSelect(row.number);
                    }}
                    className="hit flex w-full items-baseline gap-2.5 py-[9px] text-left"
                  >
                    {/* The number in its own gutter, right-flushed, as Ask
                        sets a citation: 3, 19.2 and 24.11 are different widths
                        and the eye runs down the last digit, not the first. */}
                    <span className="clause-number w-16 shrink-0 text-right text-[12px]">
                      {row.number}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px]">
                      {row.heading ?? <span className="text-[var(--color-muted)]">(untitled)</span>}
                    </span>
                    <span className="label shrink-0 text-[11px] tabular-nums">
                      {row.ahead > 0 && (
                        <span style={{ color: 'var(--color-win)' }}>{row.ahead} ahead </span>
                      )}
                      {row.behind > 0 && (
                        <span style={{ color: 'var(--color-behind)' }}>{row.behind} behind </span>
                      )}
                      {row.silent > 0 && (
                        <span style={{ color: 'var(--color-silent)' }}>{row.silent} silent</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {active && (
          <>
            <button
              type="button"
              onClick={() => {
                setOpen(null);
                onSelect('');
              }}
              className="hit label mb-4 text-[11px] underline underline-offset-2"
            >
              ← All provisions
            </button>

            {view.isLoading && <p className="text-[13px] text-[var(--color-muted)]">Loading…</p>}

            {view.data && (
              <article>
                {/* The clause number in its own gutter above a section rule,
                    the same header the reader gives a provision — this is the
                    same clause, looked at from a different side. */}
                <h1 className="mb-5 flex items-baseline gap-3 border-b border-[var(--color-rule-strong)] pb-3">
                  <span
                    className="clause-number shrink-0"
                    style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em' }}
                  >
                    {view.data.number}
                  </span>
                  <span className="register-reading" style={{ fontSize: 23, fontWeight: 600 }}>
                    {view.data.heading}
                  </span>
                </h1>

                {view.data.artefacts.length > 0 && (
                  /*
                   * Our defect, not the drafter's — and the difference matters at
                   * the table. A rep who raises an extraction artefact as a
                   * drafting problem is wrong in front of the employer, and
                   * spends credibility on our bug.
                   */
                  <div className="card mb-4" style={{ borderColor: 'var(--color-defect)' }}>
                    <p className="card-header label text-[11px]" style={{ color: 'var(--color-defect)' }}>
                      Our copy of this clause is damaged
                    </p>
                    {view.data.artefacts.map((a) => (
                      <p
                        key={a.label}
                        className="text-[12.5px] leading-snug text-[var(--color-muted)]"
                      >
                        {a.detail}
                      </p>
                    ))}
                  </div>
                )}

                {view.data.ourPlain && (
                  /* Serif at reading size. This is the only run of prose on
                     the screen a person reads rather than scans, and it was
                     set in 13px interface sans beside 13.5px serif cells that
                     quote the instruments. */
                  <p className="register-reading measure-wide mb-5 text-[15px]">
                    {view.data.ourPlain}
                    {!view.data.plainVerified && (
                      <span className="label ml-2 text-[11px] text-[var(--color-defect)]">
                        unchecked rewrite
                      </span>
                    )}
                  </p>
                )}

                {view.data.units.map((unit) => (
                  <section key={unit.number} className="mb-5">
                    <div className="mb-1.5 flex items-baseline gap-2.5">
                      {/* Ours, so it opens in Read — unlike the comparator
                          numbers in the cells, which Read does not hold. */}
                      <button
                        type="button"
                        onClick={() => onOpenClause(unit.number)}
                        className="hit clause-number text-[12px] underline decoration-[var(--color-rule-strong)] underline-offset-2"
                      >
                        {unit.number}
                      </button>
                      {unit.heading && (
                        <span className="text-[13px] font-medium">{unit.heading}</span>
                      )}
                    </div>

                    {/* 8px, the panel radius. At 6px it was drawn at the chip
                        radius while holding four columns of quoted prose. */}
                    <div className="grid grid-cols-[minmax(0,1.1fr)_repeat(3,minmax(0,1fr))] rounded-[8px] border border-[var(--color-rule)]">
                      <div className="border-r border-[var(--color-rule-strong)] p-3">
                        <span className="label">Ours</span>
                        {/* Our column carries the weight the mockup gives it:
                            this is the instrument under negotiation and the
                            other three are what it is measured against. */}
                        <p className="register-reading mt-1.5 text-[13.5px] font-medium leading-relaxed">
                          {unit.ourText}
                        </p>
                      </div>
                      {unit.comparators.map((cell) => (
                        <Cell key={cell.instrumentId} cell={cell} />
                      ))}
                    </div>
                  </section>
                ))}
              </article>
            )}
          </>
        )}
      </div>
    </div>
  );
}
